import { useEffect, useState } from "react";
import { LiaSearchPlusSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { categories, rawMaterials, Unit } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Pagination from "../../common/Pagination";
import Model from "../../common/Model";
import FormFields from "../../common/FormFields";
import ConfirmDeleteModal from "../../common/DeleteModel";
import ErrorModal from "../../common/CustomModel";
import { HiOutlinePlus } from "react-icons/hi";
import EntityAddModal from "../../common/EntityAddModal";
import Button from "../../common/Button";

const RawMaterial = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    id: 0,
    name: "",
    category_Id: "",
    unit_Id: "",
  });
  const [loading, setLoading] = useState(false);
  const [isMaterialsData, setIsMaterialsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isCategoryData, setIsCategoryData] = useState([]);
  const [isUnitData, setIsUnitData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 10;
  const [modalType, setModalType] = useState(null);

  const fetchLocationData = async () => {
    setLoading(true);
    try {
      const res = await rawMaterials.getAllRawMaterials(currentPage, pageSize);
      setIsMaterialsData(res.items || []);
      setFilteredData(res.data || []);
      setTotalRecords(res.totalCount || res.data?.length || 0);
    } catch (error) {
      notify("Error fetching location data", "error");
    }
    setLoading(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoryRes, unitRes] = await Promise.all([
        categories.getAll(),
        Unit.getAll(),
      ]);
      setIsCategoryData(categoryRes || []);
      setIsUnitData(unitRes || []);
    } catch (error) {
      notify("Error fetching location data", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLocationData();
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredData(isMaterialsData);
    } else {
      const filtered = isMaterialsData.filter((d) =>
        d.status_Name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredData(filtered);
    }
  }, [searchQuery, isMaterialsData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setIsModelOpen(true);
    setIsEditing(false);
    setFormData({
      id: 0,
      name: "",
      category_Id: "",
      unit_Id: "",
    });
  };

  const handleEdit = async (id) => {
    try {
      const data = await rawMaterials.getById(id);
      if (data) {
        setFormData({
          id: data.id,
          name: data.name,
          category_Id: data.category_Id,
          unit_Id: data.unit_Id,
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (error) {
      notify("Failed to fetch location details", "error");
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await rawMaterials.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchLocationData();
    } catch (error) {
      notify("Failed to delete record", "error");
    }
    setIsDeleteModalOpen(false);
    setSelectedId(null);
  };

  const handleSubmit = async ({ resetOnly = false }) => {
    try {
      await rawMaterials.saveOrUpdate(formData);
      notify(
        isEditing ? "Updated successfully" : "Added successfully",
        "success"
      );
      fetchLocationData();
      setFormData({
        id: 0,
        name: "",
        category_Id: "",
        unit_Id: "",
      });
      setIsEditing(false);
      if (!resetOnly) {
        setIsModelOpen(false);
      }
    } catch (error) {
      notify("Failed to submit form", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    }
  };

  const optionMap = {
    category: {
      setter: setIsCategoryData,
      fetcher: categories.getAll,
      mapFn: (data) =>
        data.map((c) => ({
          value: c.id,
          label: c.name,
        })),
      setField: "category_Id",
    },
    unit: {
      setter: setIsUnitData,
      fetcher: Unit.getAll,
      mapFn: (data) =>
        data.map((s) => ({
          value: s.unit_Id,
          label: s.unit_Name,
        })),
      setField: "unit_Id",
    },
  };

  const handleEntityAddSuccess = async (type, newOption) => {
    const config = optionMap[type];
    if (!config) return;

    try {
      const updated = await config.fetcher();
      config.setter(updated || []);
      setFormData((prev) => ({
        ...prev,
        [config.setField]: newOption?.value || "",
      }));
      setModalType(null);
    } catch (err) {
      notify("Failed to refresh dropdown", "error");
    }
  };

  const enhancedFields = [
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
      errorMessage: "Name is required",
    },
    {
      name: "category_Id",
      label: "Category Name",
      type: "selectWithAdd",
      required: true,
      options: isCategoryData.map((s) => ({
        value: s.id,
        label: s.name,
      })),
      onAddNew: () => setModalType("category"),
      errorMessage: "Category is required",
    },
    {
      name: "unit_Id",
      label: "Unit Name",
      type: "selectWithAdd",
      options: isUnitData.map((s) => ({
        value: s.unit_Id,
        label: s.unit_Name,
      })),
      onAddNew: () => setModalType("unit"),
    },
  ];

  return (
    <>
      <div className="rounded-md bg-white shadow-md border px-5 pt-6 pb-2.5  mt-10 ">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <div className="relative w-full sm:w-1/2 lg:w-1/3">
            <input
              type="text"
              className="w-full bg-gray-100 border border-gray-3 py-2 pl-10 pr-10 rounded-md focus:ring-1 focus:ring-primary focus:border-primary outline-none"
              placeholder="Search ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button
              icon={LiaSearchPlusSolid}
              iconSize="w-5 h-5"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary"
            ></Button>
            {searchQuery && (
              <Button
                icon={MdClose}
                onClick={() => setSearchQuery("")}
                iconSize="w-5 h-5"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-danger"
              ></Button>
            )}
          </div>
          <Button
            text="Add Raw Material"
            className="bg-orange text-black font-semibold text-md rounded-md p-2"
            icon={HiOutlinePlus}
            iconColor="text-black"
            iconSpacing="pr-2"
            padding="p-2"
            onClick={handleAdd}
          />
        </div>

        {loading ? (
          <Loader />
        ) : (
          <Table
            columns={[
              { key: "name", label: "Name" },
              { key: "category_Name", label: "Category Name" },
              { key: "unit_Name", label: "Unit Name" },
            ]}
            data={filteredData}
            onEdit={(row) => handleEdit(row.id)}
            onDelete={(row) => handleDelete(row.id)}
          />
        )}

        <div className="flex justify-center items-center mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalRecords / pageSize)}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Raw Material" : "Add Raw Material"}
      >
        <FormFields
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: false });
          }}
          onSubmitAndAddNew={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: true });
          }}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={enhancedFields}
          isEditing={isEditing}
          onClear={() => setIsModelOpen(false)}
        />
      </Model>
      <Model
        isOpen={!!modalType}
        onClose={() => setModalType(null)}
        name={`Add ${modalType}`}
      >
        <EntityAddModal
          type={modalType}
          onClose={(newOption) => {
            setModalType(null);
            handleEntityAddSuccess(modalType, newOption);
          }}
        />
      </Model>
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}
    </>
  );
};

export default RawMaterial;
